import React, { useMemo } from 'react';
import {
  Box,
  Checkbox,
  ChevronDownIcon,
  ChevronUpIcon,
  Inscription,
  List,
  NoSearchItemsPlaceholder,
  OverflowText,
  PaginationBar,
  SearchInput,
  SearchPlaceholderIcon,
  SelectDropdown,
  ShimmerBox,
  Text,
} from '@common-fe/common-fe';

import { OptionKey } from '@/common/types';
import ServiceCategoryIcon from '@/modules/employee/EnrollmentDetails/EnrollmentDetailsAccountInfo/EligibleServices/ServiceCategoryIcon';
import {
  SERVICE_TYPE_FILTER_OPTIONS,
  SERVICE_TYPE_LABELS,
} from '@/modules/ProcessingDefinition/Services/Services.constants';
import { CategoryItem, ServiceItem } from '@/modules/ProcessingDefinition/Services/Services.types';
import colors from '@/styles/colors';

import { CategoryCardWrapper } from '../CategoryRow.styles';
import { CHECK_STATE, CheckState } from '../hooks';

const SHIMMER_COUNT = 3;

interface CategoryRowProps {
  category: CategoryItem;
  isExpanded: boolean;
  checkState: CheckState;
  isLoadingAll: boolean;
  services: ServiceItem[];
  servicesTotal: number;
  isServicesLoading: boolean;
  searchString: string;
  serviceTypeFilter: string;
  page: number;
  perPage: number;
  isServiceSelected: (serviceId: string) => boolean;
  onToggleExpand: () => void;
  onToggleCategory: () => void;
  onToggleService: (service: ServiceItem) => void;
  onSearchChange: (value: string) => void;
  onServiceTypeChange: (value: OptionKey) => void;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
}

const CategoryRow: React.FC<CategoryRowProps> = ({
  category,
  isExpanded,
  checkState,
  isLoadingAll,
  services,
  servicesTotal,
  isServicesLoading,
  searchString,
  serviceTypeFilter,
  page,
  perPage,
  isServiceSelected,
  onToggleExpand,
  onToggleCategory,
  onToggleService,
  onSearchChange,
  onServiceTypeChange,
  onPageChange,
  onPerPageChange,
}) => {
  const shimmerItems = useMemo(
    () => Array.from({ length: SHIMMER_COUNT }, (_, i) => <ShimmerBox key={i} height="48px" width="100%" />),
    []
  );

  return (
    <CategoryCardWrapper
      $isExpanded={isExpanded}
      data-testid={`add-services-modal_category-row_${category.id}`}
      background="canvas"
      round="container2Round"
    >
      <Box
        data-testid={`add-services-modal_category-row_${category.id}_header`}
        direction="row"
        align="center"
        pad={{ vertical: 'spacing12', horizontal: 'spacing12' }}
        height="48px"
        onClick={onToggleExpand}
        style={{ cursor: 'pointer' }}
      >
        <Box direction="row" align="center" gap="spacing12" style={{ flex: 1 }}>
          {isExpanded ? <ChevronUpIcon color="textTitle" /> : <ChevronDownIcon color="textTitle" />}
          <Box onClick={(e) => e.stopPropagation()} width="24px">
            <Checkbox
              data-testId={`add-services-modal_category-checkbox_${category.id}`}
              checked={checkState === CHECK_STATE.CHECKED}
              manualIndeterminate={checkState === CHECK_STATE.INDETERMINATE}
              onChange={onToggleCategory}
              disabled={isLoadingAll}
            />
          </Box>
          <ServiceCategoryIcon type={category.name} color={colors.activity[category.name]} size="24px" />
          <Text weight="bold" color="textBody">
            {category.description || category.name}
          </Text>
        </Box>
      </Box>

      {isExpanded && (
        <Box pad={{ horizontal: 'spacing12', bottom: 'spacing12' }}>
          {isLoadingAll ? (
            <Box background="module" round="container2Round" pad="spacing12">
              <Box direction="row" justify="between" align="center" pad={{ bottom: 'spacing8' }}>
                <Inscription weight="bold" color="textBody" lineHeight="20px">
                  Services: {servicesTotal}
                </Inscription>
                <Box direction="row" gap="spacing12">
                  <Box width="200px">
                    <SearchInput
                      testId={`add-services-modal_search_${category.id}`}
                      value={searchString}
                      placeholder="Search"
                      onChange={onSearchChange}
                      disabled
                    />
                  </Box>
                  <Box width="160px">
                    <SelectDropdown
                      id={`serviceType-${category.id}`}
                      singleMode
                      testId={`add-services-modal_type-filter_${category.id}`}
                      ellipsisMode
                      activeTitle
                      name="Type"
                      prefix="Type"
                      options={SERVICE_TYPE_FILTER_OPTIONS}
                      value={serviceTypeFilter}
                      onChange={onServiceTypeChange}
                      disabled
                    />
                  </Box>
                </Box>
              </Box>
              <Box gap="spacing4">{shimmerItems}</Box>
              {servicesTotal > perPage && (
                <Box style={{ opacity: 0.5, pointerEvents: 'none' }}>
                  <PaginationBar
                    page={page}
                    total={servicesTotal}
                    pageSize={perPage}
                    onChangePage={onPageChange}
                    onChangePageSize={onPerPageChange}
                    hideJumpToPage
                    hideTotalResult
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Box background="module" round="container2Round" pad="spacing12">
              {/* Controls: Services count + Search + Type filter */}
              <Box direction="row" justify="between" align="center" pad={{ bottom: 'spacing8' }}>
                <Inscription weight="bold" color="textBody" lineHeight="20px">
                  Services: {servicesTotal}
                </Inscription>
                <Box direction="row" gap="spacing12">
                  <Box width="200px">
                    <SearchInput
                      testId={`add-services-modal_search_${category.id}`}
                      value={searchString}
                      placeholder="Search"
                      onChange={onSearchChange}
                    />
                  </Box>
                  <Box width="160px">
                    <SelectDropdown
                      id={`serviceType-${category.id}`}
                      singleMode
                      testId={`add-services-modal_type-filter_${category.id}`}
                      ellipsisMode
                      activeTitle
                      name="Type"
                      prefix="Type"
                      options={SERVICE_TYPE_FILTER_OPTIONS}
                      value={serviceTypeFilter}
                      onChange={onServiceTypeChange}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Services list */}
              <List>
                {isServicesLoading ? (
                  <List.Preloader
                    testId={`add-services-modal_services-loading_${category.id}`}
                    elementsHeight="48px"
                    count={3}
                  />
                ) : (
                  <>
                    {services.length === 0 && (
                      <Box margin={{ vertical: 'spacing6' }}>
                        <NoSearchItemsPlaceholder
                          placeholderTitle="No services found"
                          node={<SearchPlaceholderIcon size="xxlarge" color="iconSecondary" />}
                          style={{ height: 'auto' }}
                        />
                      </Box>
                    )}
                    {services.map((service) => (
                      <List.Item
                        key={service.id}
                        data-testid={`add-services-modal_service-item_${service.id}`}
                        height="48px"
                        pad={{ horizontal: 'spacing24' }}
                        margin={{ top: 'spacing4' }}
                        onClick={() => onToggleService(service)}
                      >
                        <Box direction="row" align="center" style={{ flex: 6 }}>
                          <Box onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              data-testId={`add-services-modal_service-checkbox_${service.id}`}
                              checked={isServiceSelected(service.id)}
                              onChange={() => onToggleService(service)}
                            />
                          </Box>
                          <OverflowText title={service.name} textStyle={{ fontWeight: 'normal' }} />
                        </Box>
                        <Box style={{ flex: 3 }} align="end">
                          <Text size="medium" color="textDisabled" weight={400}>
                            {SERVICE_TYPE_LABELS[service.serviceType] || service.serviceType}
                          </Text>
                        </Box>
                      </List.Item>
                    ))}
                  </>
                )}
              </List>

              {/* Pagination */}
              {servicesTotal > perPage && (
                <PaginationBar
                  page={page}
                  total={servicesTotal}
                  pageSize={perPage}
                  onChangePage={onPageChange}
                  onChangePageSize={onPerPageChange}
                  hideJumpToPage
                  hideTotalResult
                />
              )}
            </Box>
          )}
        </Box>
      )}
    </CategoryCardWrapper>
  );
};

export default CategoryRow;
